import type { ChatMessagePart, ChatScenarioData, ChatScenarioMessage, ChatTextLikePart } from "./types";

const MAX_TRANSCRIPT_CHARS = 12_000;

/**
 * Extracts all spoken text from an assistant message's parts (text + speech only, no tool calls).
 * Returns `null` when the message has no speakable content.
 */
export function extractAssistantSpeechText(message: ChatScenarioMessage): string | null {
  const text = message.parts
    .filter((p): p is ChatTextLikePart => p.type === "text" || p.type === "speech")
    .map(p => p.content.trim())
    .filter(Boolean)
    .join(" ");
  return text || null;
}

/**
 * Scans forward through `messages` starting at `fromIndex` and returns the spoken text of the
 * first assistant turn that has speakable content, or `null` if none is found.
 */
export function findNextAssistantSpeechLine(messages: ChatScenarioMessage[], fromIndex: number): string | null {
  for (let i = fromIndex; i < messages.length; i++) {
    if (messages[i].role === "assistant") {
      const text = extractAssistantSpeechText(messages[i]);
      if (text) return text;
    }
  }
  return null;
}

/**
 * Returns the spoken text of the last assistant turn before `beforeIndex`, or `null` if none.
 */
export function findLastAssistantSpeechLine(messages: ChatScenarioMessage[], beforeIndex: number): string | null {
  const end = Math.min(beforeIndex, messages.length);
  for (let i = end - 1; i >= 0; i--) {
    if (messages[i].role === "assistant") {
      const text = extractAssistantSpeechText(messages[i]);
      if (text) return text;
    }
  }
  return null;
}

/**
 * Builds the agent's opening voice line when handing off from a scripted text preview.
 * Uses `settledMessageCount` (fully revealed messages), not mounted/typing bubbles — in
 * `same-bubble` mode the user bubble mounts while still showing typing dots.
 */
export function buildPreviewHandoffFirstMessage(
  messages: ChatScenarioMessage[],
  settledMessageCount: number,
  followUpQuestion: string,
): string | null {
  if (settledMessageCount <= 0) {
    const firstLine = findNextAssistantSpeechLine(messages, 0);
    return firstLine ? `${firstLine} ${followUpQuestion}` : null;
  }

  const lastSettled = messages[settledMessageCount - 1];

  if (lastSettled?.role === "assistant") {
    const lastLine = findLastAssistantSpeechLine(messages, settledMessageCount);
    return lastLine ? `${lastLine} ${followUpQuestion}` : null;
  }

  const nextLine = findNextAssistantSpeechLine(messages, settledMessageCount);
  return nextLine ? `${nextLine} ${followUpQuestion}` : null;
}

function formatPart(part: ChatMessagePart): string | null {
  if (part.type === "text" || part.type === "speech") {
    return part.content.trim() || null;
  }

  if (part.type === "tool" && part.name === "simple") {
    const text = part.data.successText?.trim() || part.data.loadingText?.trim();
    return text ? `[${text}]` : null;
  }

  return null;
}

function formatMessage(message: ChatScenarioMessage, assistantLabel: string): string | null {
  const label = message.role === "assistant" ? assistantLabel : "User";
  const segments = message.parts.map(formatPart).filter((s): s is string => Boolean(s));
  return segments.length > 0 ? `${label}: ${segments.join(" ")}` : null;
}

/**
 * Serializes a `ChatScenarioData` into a plain-text transcript suitable for an ElevenLabs
 * `dynamicVariables` context field.
 *
 * Returns `null` when no messages produce any output (empty scenario or all parts are blank).
 * Callers should skip sending the variable entirely when `null` is returned.
 *
 * @param scenario - The scripted scenario to serialize.
 * @param options.assistantLabel - Label for assistant turns (default `"Assistant"`).
 * @param options.messageLimit - Cap the number of messages serialized (default: all).
 */
export function formatChatScenarioForAgent(
  scenario: ChatScenarioData,
  options?: { assistantLabel?: string; messageLimit?: number },
): string | null {
  const assistantLabel = options?.assistantLabel ?? "Assistant";
  const allMessages = [...(scenario.initialMessages ?? []), ...scenario.messages];
  const limit =
    typeof options?.messageLimit === "number" && options.messageLimit > 0
      ? Math.floor(options.messageLimit)
      : allMessages.length;

  const lines = allMessages
    .slice(0, limit)
    .map(m => formatMessage(m, assistantLabel))
    .filter((line): line is string => Boolean(line));

  let transcript = lines.join("\n");

  if (transcript.length > MAX_TRANSCRIPT_CHARS) {
    transcript = transcript.slice(transcript.length - MAX_TRANSCRIPT_CHARS);
  }

  return transcript.length > 0 ? transcript : null;
}
